import { ref, computed } from 'vue'
import { useChatSocket } from './useChatSocket'
import { useChatAudio } from './useChatAudio'

// Pin state for the global thread. Singleton (one pin per thread). The
// renderer surfaces this as a sticky banner above the message list.
// Module-scope so every consumer of useGlobalChat shares the same ref.
const pinnedMessageId = ref<string | null>(null)
const pinnedAt = ref<string | null>(null)
const pinnedBy = ref<string | null>(null)

/**
 * Global tenant chat room state + actions.
 *
 * Source of truth is REST (`/api/chat/global`) for history; WS `message.new`
 * and `message.delete` frames keep it live. On reconnect we re-pull the
 * newest page to reconcile any events missed while disconnected.
 *
 * Messages are stored newest-last in the array so MessageList can render
 * top-down (older at top, newest at bottom) — matches chat convention.
 */

interface ChatUserPublic { id: string; name: string; avatar: string | null; role: string }

export type ChatBodyNode =
    | { type: 'text'; value: string }
    | { type: 'url'; value: string }
    | { type: 'emoji'; name: string; url: string }

export interface ChatMessage {
    id: string
    threadId: string
    kind: 'global' | 'dm'
    author: ChatUserPublic
    body: string
    /** Pre-rendered span list from the server. Optimistic / pre-migration
     *  messages may not have it; ChatMessageItem falls back to a
     *  client-side parse in those cases. */
    bodyAst?: ChatBodyNode[]
    attachments: any[]
    links: any[]
    deletedAt: string | null
    editedAt: string | null
    createdAt: string
    // Client-side fields
    clientMsgId?: string
    sendState?: 'sending' | 'sent' | 'failed'
    sendError?: string | null
}

const messages = ref<ChatMessage[]>([])
const threadId = ref<string | null>(null)
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(false)
const sending = ref(false)

let wired = false
let inited = false

function mergeMessageInPlace(list: ChatMessage[], incoming: ChatMessage) {
    // Dedup by id OR clientMsgId (for ack reconciliation)
    const idx = list.findIndex(m =>
        m.id === incoming.id ||
        (incoming.clientMsgId && m.clientMsgId === incoming.clientMsgId)
    )
    if (idx >= 0) {
        // Preserve any client-only fields that aren't in the server version
        list[idx] = { ...list[idx], ...incoming }
    } else {
        list.push(incoming)
    }
}

function wire() {
    if (wired) return
    wired = true
    const { on } = useChatSocket()
    const { playMessage } = useChatAudio()
    const { user } = useUserSession()

    on('message.new', (frame) => {
        if (!frame?.message || frame.message.kind !== 'global') return
        mergeMessageInPlace(messages.value, frame.message)

        // Audio notification — skip own messages, debounce per sender.
        const meId = String((user.value as any)?._id || (user.value as any)?.id || '')
        const authorId = String(frame.message.author?.id || '')
        if (authorId && authorId !== meId) {
            playMessage(authorId).catch(() => { /* noop */ })
        }
    })

    on('message.delete', (frame) => {
        if (!frame?.messageId) return
        // The WS multiplexes all rooms onto one socket; a delete frame
        // for a bug-report message would otherwise match a same-id row
        // here and wipe it from the global feed. Filter by kind when
        // the frame carries one (server adds it; legacy frames without
        // kind fall through to the previous behaviour for safety).
        if (frame.kind && frame.kind !== 'global') return
        const msg = messages.value.find(m => m.id === frame.messageId)
        if (msg) {
            msg.deletedAt = new Date().toISOString()
            msg.body = ''
            msg.attachments = []
            msg.links = []
        }
    })

    // Admin cleared the whole global chat — drop everything in the local
    // list. Keep any in-flight optimistic sends so the user doesn't lose
    // their own unsent draft because an admin hit Clear mid-compose.
    // Also reset the pinned banner: the server's clear endpoint nulls
    // Thread.pinnedMessage but doesn't broadcast a separate
    // `thread.pinUpdated` frame, so the client must self-reset here to
    // avoid a stale sticky banner that scrolls to a deleted message.
    on('chat.global.cleared', () => {
        messages.value = messages.value.filter(
            m => m.sendState === 'sending' || m.sendState === 'failed'
        )
        hasMore.value = false
        pinnedMessageId.value = null
        pinnedAt.value = null
        pinnedBy.value = null
    })

    on('message.linkPreview', (frame) => {
        // Server occasionally emits a partial frame (messageId undefined when
        // the OG fetch raced the message delete). Bail before touching `find`
        // on a possibly-missing `links` array.
        if (!frame?.messageId) return
        const msg = messages.value.find(m => m.id === frame.messageId)
        if (!msg) return
        const link = msg.links?.find((l: any) => l.url === frame.url)
        if (link) link.preview = frame.preview
    })

    // Edited message — drop-in replace from the freshly-serialised frame.
    on('message.updated', (frame) => {
        if (!frame?.message?.id) return
        const idx = messages.value.findIndex(m => m.id === frame.message.id)
        if (idx >= 0) Object.assign(messages.value[idx], frame.message)
    })

    // Reaction add/remove — re-bucket locally so the wire payload stays
    // tiny. `mine` flag flips based on the viewer id from useUserSession.
    const meIdLocal = () => {
        const u = useUserSession().user.value as any
        return String(u?._id || u?.id || '')
    }
    function applyReactionDelta(frame: any, delta: 1 | -1) {
        if (!frame?.messageId || !frame?.emoji) return
        const msg = messages.value.find(m => m.id === frame.messageId)
        if (!msg) return
        const reactions = ((msg as any).reactions = (msg as any).reactions || [])
        const mine = String(frame.userId) === meIdLocal()
        const existing = reactions.find((r: any) => r.emoji === frame.emoji)
        if (delta > 0) {
            if (existing) {
                existing.count += 1
                if (mine) existing.mine = true
            } else {
                reactions.push({ emoji: frame.emoji, count: 1, mine })
            }
        } else {
            if (!existing) return
            existing.count = Math.max(0, existing.count - 1)
            if (mine) existing.mine = false
            if (existing.count <= 0) {
                const i = reactions.indexOf(existing)
                if (i >= 0) reactions.splice(i, 1)
            }
        }
    }
    on('message.reaction.added', (frame) => applyReactionDelta(frame, 1))
    on('message.reaction.removed', (frame) => applyReactionDelta(frame, -1))

    // Pinned-message banner update (admin-only on global). Re-exposed via
    // useGlobalChat's `pinnedMessageId` ref so ChatGlobalRoom can render
    // the sticky banner.
    on('thread.pinUpdated', (frame) => {
        pinnedMessageId.value = frame?.pinnedMessageId || null
        pinnedAt.value = frame?.pinnedAt || null
        pinnedBy.value = frame?.pinnedBy || null
    })

    // Resync on reconnect — re-pull newest page, merge
    on('__open', () => {
        if (inited) loadInitial()
    })
}

async function loadInitial() {
    loading.value = true
    try {
        const res = await $fetch<{ threadId: string; messages: ChatMessage[]; hasMore: boolean }>('/api/chat/global')
        threadId.value = res.threadId
        // Server returns newest-first — reverse for chronological render
        const chronological = [...res.messages].reverse()
        // Merge with any optimistic sends we have pending
        const pendingLocal = messages.value.filter(m => m.sendState === 'sending' || m.sendState === 'failed')
        messages.value = chronological
        for (const p of pendingLocal) mergeMessageInPlace(messages.value, p)
        hasMore.value = res.hasMore
        inited = true
    } catch (err: any) {
        if (err?.statusCode === 404) inited = true // feature disabled
        else console.error('[chat:global] load failed', err)
    } finally {
        loading.value = false
    }
}

async function loadOlder() {
    if (loadingMore.value || !hasMore.value) return
    const oldest = messages.value[0]
    if (!oldest) return
    loadingMore.value = true
    try {
        const res = await $fetch<{ messages: ChatMessage[]; hasMore: boolean }>('/api/chat/global', {
            params: { before: oldest.createdAt }
        })
        const older = [...res.messages].reverse()
        messages.value = [...older, ...messages.value]
        hasMore.value = res.hasMore
    } catch (err) {
        console.error('[chat:global] load older failed', err)
    } finally {
        loadingMore.value = false
    }
}

function makeClientMsgId(): string {
    return 'cm_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
}

async function sendText(text: string, currentUser: ChatUserPublic, attachments: any[] = [], replyToId: string | null = null): Promise<void> {
    const trimmed = text.trim()
    if (!trimmed && attachments.length === 0) return

    const clientMsgId = makeClientMsgId()
    const optimistic: ChatMessage = {
        id: clientMsgId,
        clientMsgId,
        threadId: threadId.value || '',
        kind: 'global',
        author: currentUser,
        body: trimmed,
        attachments,
        links: [],
        deletedAt: null,
        editedAt: null,
        createdAt: new Date().toISOString(),
        sendState: 'sending',
    }
    messages.value.push(optimistic)
    sending.value = true

    try {
        const res = await $fetch<{ ok: boolean; message: ChatMessage; clientMsgId?: string }>('/api/chat/global', {
            method: 'POST',
            body: { body: trimmed, attachments, clientMsgId, replyTo: replyToId }
        })
        const idx = messages.value.findIndex(m => m.clientMsgId === clientMsgId)
        if (idx >= 0) {
            messages.value[idx] = { ...res.message, clientMsgId, sendState: 'sent' }
        } else {
            mergeMessageInPlace(messages.value, { ...res.message, clientMsgId, sendState: 'sent' })
        }
    } catch (err: any) {
        const msg = messages.value.find(m => m.clientMsgId === clientMsgId)
        if (msg) {
            msg.sendState = 'failed'
            msg.sendError = err?.data?.statusMessage || err?.statusMessage || err?.message || 'send_failed'
        }
    } finally {
        sending.value = false
    }
}

async function retrySend(clientMsgId: string, currentUser: ChatUserPublic): Promise<void> {
    const msg = messages.value.find(m => m.clientMsgId === clientMsgId)
    if (!msg || msg.sendState !== 'failed') return
    const body = msg.body
    const attachments = msg.attachments
    messages.value = messages.value.filter(m => m.clientMsgId !== clientMsgId)
    await sendText(body, currentUser, attachments)
}

export function useGlobalChat() {
    wire()
    return {
        messages: computed(() => messages.value),
        threadId: computed(() => threadId.value),
        loading: computed(() => loading.value),
        loadingMore: computed(() => loadingMore.value),
        hasMore: computed(() => hasMore.value),
        sending: computed(() => sending.value),
        inited: () => inited,
        loadInitial,
        loadOlder,
        sendText,
        retrySend,
        pinnedMessageId: computed(() => pinnedMessageId.value),
        pinnedAt: computed(() => pinnedAt.value),
        pinnedBy: computed(() => pinnedBy.value),
    }
}
